/**
 * PreferredVideoModelsSection — Global Config multi-select for the project's
 * preferred video models (soft allowlist the Director picks from).
 *
 * - Chips for every selectable native model plus the user's enabled BYOM models.
 * - Saved IDs that are no longer selectable (deleted/disabled BYOM, revoked
 *   internal model) stay visible as disabled "(unavailable)" chips so the user
 *   can deselect them — they are never silently dropped.
 * - Empty selection (or all-unavailable) == Dreamwrite default, shown as a hint.
 *
 * Order is click order: the first selected model is the Director's default.
 * Persistence is owned by the parent (`ProjectConfigGear`) so the save/toast
 * sequencing stays in one place.
 *
 * Feature tag: `project-setup-card` (concern `preferred-video-models`).
 */
import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useVideoModels } from '../../hooks/useVideoModels'
import { createNadDebug } from '../../utils/debugLog'
import { normalizeVideoPreferences, videoPreferenceLabel } from './videoFamilyPreference'
import { resolveVideoModelRow } from './videoModelRow'

const log = createNadDebug('dw:project-setup-card:preferred-video-models')

interface ModelChip {
  id: string
  label: string
  selectable: boolean
}

export interface PreferredVideoModelsSectionProps {
  preferred: string[]
  effectiveDefault: string | null
  /** False when `effectiveDefault` is an enabled default My Video Model that cannot run. */
  effectiveDefaultRunnable?: boolean
  disabled?: boolean
  /** Render a collapsed selector that expands to the existing ordered chip picker. */
  compact?: boolean
  onChange: (next: string[]) => void
}

export function togglePreferredModel(preferred: string[], id: string): string[] {
  return preferred.includes(id) ? preferred.filter((value) => value !== id) : [...preferred, id]
}

export const PreferredVideoModelsSection = memo(function PreferredVideoModelsSection({
  preferred: savedPreferred,
  effectiveDefault,
  effectiveDefaultRunnable = true,
  disabled = false,
  compact = false,
  onChange,
}: PreferredVideoModelsSectionProps) {
  const { t } = useTranslation('canvas')
  const [isCompactOpen, setIsCompactOpen] = useState(false)
  const preferred = useMemo(() => normalizeVideoPreferences(savedPreferred), [savedPreferred])
  const { native, byom, defaultModel, isReady } = useVideoModels()

  const { nativeChips, byomChips, staleChips } = useMemo(() => {
    const enabledByom = byom.filter((model) => model.enabled)
    const nativeFamilies = normalizeVideoPreferences(native)
    const selectableIds = new Set([...nativeFamilies, ...enabledByom.map((model) => model.id)])
    const toChip = (id: string): ModelChip => ({
      id,
      label: videoPreferenceLabel(id, byom),
      selectable: true,
    })
    return {
      nativeChips: nativeFamilies.map(toChip),
      byomChips: enabledByom.map((model) => toChip(model.id)),
      // Only flag stale IDs once the catalog has loaded; before that every
      // saved ID would look unavailable.
      staleChips: isReady
        ? preferred
            .filter((id) => !selectableIds.has(id))
            .map((id) => ({ ...toChip(id), selectable: false }))
        : [],
    }
  }, [byom, isReady, native, preferred])

  // Removing one stale chip at a time can never succeed when two or more are
  // stale: the backend (correctly) rejects any list that still contains an
  // unavailable ID. Drop them all in one write instead.
  const handleRemoveUnavailable = () => {
    const staleIds = new Set(staleChips.map((chip) => chip.id))
    const next = preferred.filter((id) => !staleIds.has(id))
    log({ event: 'remove_unavailable', removed: [...staleIds].join(','), next: next.join(',') })
    onChange(next)
  }

  const handleToggle = (id: string) => {
    const next = togglePreferredModel(preferred, id)
    log({ event: 'toggle', model_id: id, selected: next.includes(id), next: next.join(',') })
    onChange(next)
  }

  const renderChip = (chip: ModelChip) => {
    const index = preferred.indexOf(chip.id)
    const checked = index >= 0
    const label = chip.selectable
      ? chip.label
      : t('projectConfigGear.preferredVideoModelsUnavailable', { name: chip.label })
    return (
      <button
        key={chip.id}
        type="button"
        role="checkbox"
        aria-checked={checked}
        // Stale chips stay clickable while selected so the user can remove them.
        disabled={disabled || (!chip.selectable && !checked)}
        onClick={() => handleToggle(chip.id)}
        data-testid={`project-config-preferred-video-model-${chip.id}`}
        className={cn(
          'inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
          'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
          'focus:outline-none focus:ring-2 focus:ring-primary/20',
          checked
            ? 'border-primary bg-surface-2 text-foreground'
            : 'border-input-border bg-surface-1 text-muted-foreground hover:bg-surface-3 hover:text-foreground',
          !chip.selectable && 'line-through',
        )}
      >
        {checked && <Check className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />}
        <span className="truncate">{label}</span>
        {checked && preferred.length > 1 && (
          <span className="text-xxs text-muted-foreground">#{index + 1}</span>
        )}
      </button>
    )
  }

  // Same resolution as the chat setup card: an empty preference, or one whose
  // entries are all unavailable, names the Dreamwrite default.
  const row = resolveVideoModelRow(savedPreferred, effectiveDefault, defaultModel, effectiveDefaultRunnable)
  const options = (
    <>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('projectConfigGear.preferredVideoModels')}>
        {nativeChips.map(renderChip)}
        {staleChips.map(renderChip)}
      </div>
      {staleChips.length > 0 && (
        <button
          type="button"
          disabled={disabled}
          onClick={handleRemoveUnavailable}
          data-testid="project-config-preferred-video-models-remove-unavailable"
          className="mt-2 cursor-pointer text-xxs text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('projectConfigGear.preferredVideoModelsRemoveUnavailable')}
        </button>
      )}
      {byomChips.length > 0 && (
        <>
          <p className="mb-1.5 mt-2.5 text-xxs uppercase tracking-wider text-muted-foreground">
            {t('projectConfigGear.preferredVideoModelsMyModels')}
          </p>
          <div className="flex flex-wrap gap-1.5">{byomChips.map(renderChip)}</div>
        </>
      )}
      {row.kind === 'default' && (!compact || row.runnable) && (
        <p className="mt-2 text-xxs text-muted-foreground" data-testid="project-config-preferred-video-models-default">
          {t(
            row.runnable
              ? 'projectConfigGear.preferredVideoModelsDefault'
              : 'projectConfigGear.preferredVideoModelsDefaultNotRunnable',
            { name: videoPreferenceLabel(row.id, byom) },
          )}
        </p>
      )}
    </>
  )

  if (compact) {
    const compactLabel = preferred.length > 0
      ? preferred.map((id) => videoPreferenceLabel(id, byom)).join(', ')
      : t('projectConfigGear.preferredVideoModelsAuto')
    return (
      <div className="space-y-2" data-testid="project-config-preferred-video-models">
        <p className="text-base font-normal text-foreground">{t('projectConfigGear.preferredVideoModels')}</p>
        <button
          type="button"
          aria-expanded={isCompactOpen}
          aria-controls="project-config-preferred-video-model-options"
          disabled={disabled}
          onClick={() => setIsCompactOpen((open) => !open)}
          data-testid="project-config-preferred-video-models-trigger"
          className={cn(
            'flex h-9 w-full items-center justify-between gap-3 rounded-xl border border-input-border bg-input-bg px-3 text-left text-base text-foreground',
            'cursor-pointer transition-colors hover:border-input-border-hover hover:bg-surface-3',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <span className="truncate">{compactLabel}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', isCompactOpen && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
        {row.kind === 'default' && !row.runnable && (
          <p
            role="alert"
            className="text-sm text-destructive"
            data-testid="project-config-preferred-video-models-default"
          >
            {t('projectConfigGear.preferredVideoModelsDefaultNotRunnable', {
              name: videoPreferenceLabel(row.id, byom),
            })}
          </p>
        )}
        {isCompactOpen && (
          <div
            id="project-config-preferred-video-model-options"
            className="rounded-xl border border-input-border bg-input-bg p-3"
          >
            {options}
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          {t('projectConfigGear.preferredVideoModelsDescription')}
        </p>
      </div>
    )
  }

  return (
    <div
      className="rounded-lg border border-input-border bg-input-bg p-3"
      data-testid="project-config-preferred-video-models"
    >
      <p className="text-xs font-medium text-foreground">{t('projectConfigGear.preferredVideoModels')}</p>
      <p className="mb-2 text-xxs text-muted-foreground">
        {t('projectConfigGear.preferredVideoModelsDescription')}
      </p>
      {options}
    </div>
  )
})
